import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IssueEnvironment, MODE_LABEL, ScanMode, ScanScope, Stage, SweeperStatus, SweepRound } from '../../api/issues';
import { ago, environmentKey, moment } from './issueFormat';
import { takeOverSaved } from './scopeEditorModel';

export interface ISweeperPanelProps {
    status: SweeperStatus | null;
    error: string | null;
    busy: boolean;
    /** The environments a round started here scans: production, test, or all of them. */
    stage?: Stage | '';
    /** Every environment, to choose from for a timer that scans only some. */
    environments: IssueEnvironment[];
    onRun: (resume: boolean) => void;
    onScope: (mode: ScanMode, environments: string[]) => void;
    onStop: () => void;
    /** Pauses (true) or resumes (false) the timer; a round that runs now goes on. */
    onPause: (paused: boolean) => void;
    /** Opens the requeue proposals. */
    onProposals: () => void;
}

const duration = (ms: number): string => {
    if (ms < 1000) {
        return `${ms} ms`;
    }

    const seconds = Math.round(ms / 1000);

    return seconds < 120 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;
};

/**
 * The scanner at a glance: what it does right now, how far it got, when it runs again, what
 * the last rounds found and where it failed - with stop, continue and start.
 */
export const SweeperPanel = ({ status, error, busy, stage, environments, onRun, onStop, onPause, onProposals, onScope }: ISweeperPanelProps) => {
    // The switch at the top of the page decides which environments a round started here scans.
    const only = stage === 'prod' ? ' (alleen prod)' : stage === 'test' ? ' (alleen test)' : '';
    const scope = only ? `Scant alleen de ${stage === 'prod' ? 'productie' : 'test'}omgevingen, zoals bovenaan gekozen.` : 'Scant alle omgevingen.';

    const round = status?.round ?? null;
    const running = round?.state === 'Running' && !round.stale;
    const stopped = round?.state === 'Stopped' || round?.stale === true;
    const percent = round?.environments ? Math.round((100 * round.done) / round.environments) : 0;
    const paused = status?.pause?.paused === true;

    return (
        <section className={`scanner scanner--${running ? 'running' : stopped ? 'stopped' : round?.failed ? 'error' : 'idle'}`} aria-label="Scanner">
            <div className="scanner__head">
                <div className="scanner__title">
                    <FontAwesomeIcon icon="rotate" spin={running} />
                    <h3>Scanner</h3>
                    {status && <State status={status} round={round} running={running} stopped={stopped} />}
                </div>
                <div className="issues__actions">
                    {status?.enabled && (
                        <button
                            type="button"
                            className={`btn btn-sm ${paused ? 'btn-warning' : 'btn-outline-secondary'}`}
                            onClick={() => onPause(!paused)}
                            disabled={busy}
                            title={
                                paused
                                    ? 'De timer scant weer elke ronde.'
                                    : 'De timer slaat zijn rondes over tot je hervat. Een ronde die nu loopt gaat door; met de hand scannen kan nog steeds.'
                            }
                        >
                            <FontAwesomeIcon icon={paused ? 'play' : 'pause'} /> {paused ? 'Scanner hervatten' : 'Scanner pauzeren'}
                        </button>
                    )}
                    {running ? (
                        <button type="button" className="btn btn-sm btn-danger" onClick={onStop} disabled={busy}>
                            <FontAwesomeIcon icon="xmark" /> Stoppen
                        </button>
                    ) : stopped ? (
                        <>
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => onRun(true)} disabled={busy} title={scope}>
                                <FontAwesomeIcon icon="arrow-right" /> Doorgaan{only}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onRun(false)} disabled={busy} title={scope}>
                                <FontAwesomeIcon icon="rotate" /> Opnieuw scannen{only}
                            </button>
                        </>
                    ) : (
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => onRun(false)} disabled={busy || !status} title={scope}>
                            <FontAwesomeIcon icon="rotate" spin={busy} /> Nu scannen{only}
                        </button>
                    )}
                </div>
            </div>

            {error && <p className="issues__error">{error}</p>}

            {round && running && (
                <div className="scanner__progress">
                    <div className="scanner__bar" aria-hidden="true">
                        <span style={{ width: `${percent}%` }} />
                    </div>
                    <div className="scanner__progress-text">
                        <strong>
                            {round.done} van {round.environments} omgevingen ({percent}%)
                        </strong>
                        {round.working.length > 0 && (
                            // The one it started last; with several side by side the rest are in the tooltip.
                            <span className="scanner__current" title={round.working.join(', ')}>
                                {round.working[round.working.length - 1]}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {round && (
                <div className="scanner__tiles">
                    <Tile label="nieuwe issues" value={round.found} tone="info" hint="Objecten die deze ronde voor het eerst misgingen." />
                    <Tile label="opgelost" value={round.resolved} tone="ok" hint="Issues die deze ronde aantoonbaar in Exact kwamen." />
                    <Tile label="weer mislukt" value={round.reopened} tone="warn" hint="Issues die al opgelost waren en opnieuw faalden." />
                    <Tile
                        label="nieuw voorgesteld, wacht op jou"
                        value={round.proposed}
                        tone="info"
                        hint="Objecten die een nieuwe poging oplost, maar waarbij iemand eerst moet kijken, zoals betalingen. Keur ze goed op het tabblad Opnieuw aanbieden."
                        onClick={onProposals}
                    />
                    <Tile
                        label="opnieuw aangeboden"
                        value={round.requeued}
                        tone="info"
                        hint="Objecten die deze ronde op de queue gingen: wat iemand goedkeurde en wat de scanner zelf mag, in porties per omgeving."
                    />
                    <Tile label="omgevingen mislukt" value={round.failed} tone={round.failed > 0 ? 'error' : 'muted'} hint="Omgevingen die de scanner niet kon lezen; zie hieronder." />
                </div>
            )}

            {round && !running && (
                <p className="issues__muted">
                    {round.state === 'Stopped' ? 'Gestopte ronde' : 'Laatste ronde'}: {moment(round.startedAt)} ({ago(round.startedAt)}),{' '}
                    {round.trigger}, {round.done} van {round.environments} omgevingen in {duration(round.durationMs)}.
                </p>
            )}

            {round?.error && (
                <p className={round.state === 'Stopped' ? 'scanner__note' : 'issues__error'}>
                    <FontAwesomeIcon icon={round.state === 'Stopped' ? 'circle-info' : 'triangle-exclamation'} /> {round.error}
                </p>
            )}

            {status && status.failing.length > 0 && (
                <div className="scanner__failing">
                    <strong>
                        <FontAwesomeIcon icon="triangle-exclamation" /> {status.failing.length} omgeving(en) konden niet gescand worden
                    </strong>
                    <ul>
                        {status.failing.map((e) => (
                            <li key={`${e.customer}/${e.environment}`}>
                                <span className="scanner__env">
                                    {e.customer} / {e.environment}
                                </span>
                                <span className="scanner__message">{e.lastError}</span>
                                <span className="issues__muted">{moment(e.lastRunAt)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {status && <ScopeEditor scope={status.scope} environments={environments} busy={busy} onSave={onScope} />}

            {status && (
                <dl className="scanner__settings scanner__settings--list">
                    <div>
                        <dt>Scant</dt>
                        <dd>
                            elke {status.intervalMinutes} minuten{status.enabled ? '' : ' (timer staat uit)'}, {status.scope.description}
                            {(status.optedIn?.length ?? 0) > 0 && ` + ${status.optedIn?.join(', ')}`}
                        </dd>
                    </div>
                    <div>
                        <dt>Opnieuw aanbieden</dt>
                        <dd>
                            {MODE_LABEL[status.autoRequeue] ?? status.autoRequeue}
                            {status.autoRequeueChosen ? ` (gekozen door ${status.autoRequeueChangedBy ?? 'onbekend'})` : ''}
                            {!status.allowRequeue && '. Proefdraai: er wordt niets verstuurd'}
                        </dd>
                    </div>
                    <div>
                        <dt>Grenzen</dt>
                        <dd>
                            {status.maxRequeuesPerRound} per omgeving per ronde. Alleen jonger dan {status.autoRequeueMaxAgeDays} dagen. Per object hooguit{' '}
                            {status.maxAutoRequeues} keer.
                        </dd>
                    </div>
                    <div>
                        <dt>Opslag</dt>
                        <dd>{status.storage}</dd>
                    </div>
                    <div>
                        <dt>Teams</dt>
                        <dd>{status.teamsAlerts ? 'aan' : 'niet ingesteld'}</dd>
                    </div>
                </dl>
            )}

            {status && status.recent.length > 0 && (
                <details>
                    <summary className="issues__muted">Vorige rondes</summary>
                    <div className="issues__scroll">
                        <table className="issues__table">
                            <thead>
                                <tr>
                                    <th>Gestart</th>
                                    <th>Door</th>
                                    <th>Afloop</th>
                                    <th className="issues__num">Omgevingen</th>
                                    <th className="issues__num">Mislukt</th>
                                    <th className="issues__num" title="Nieuwe issues">Nieuw</th>
                                    <th className="issues__num">Opgelost</th>
                                    <th className="issues__num" title="Opgelost en opnieuw mislukt">Weer mislukt</th>
                                    <th className="issues__num" title="Klaargezet of automatisch opnieuw aangeboden">Opnieuw aanbieden</th>
                                    <th className="issues__num">Duur</th>
                                </tr>
                            </thead>
                            <tbody>
                                {status.recent.map((r) => (
                                    <tr key={r.startedAt ?? ''}>
                                        <td>{moment(r.startedAt)}</td>
                                        <td>{r.trigger}</td>
                                        <td>{r.stale ? 'onderbroken' : r.state === 'Stopped' ? 'gestopt' : r.state === 'Failed' ? 'mislukt' : 'klaar'}</td>
                                        <td className="issues__num">
                                            {r.done}/{r.environments}
                                        </td>
                                        <td className={`issues__num${r.failed > 0 ? ' text-danger' : ''}`}>{r.failed}</td>
                                        <td className="issues__num">{r.found}</td>
                                        <td className="issues__num">{r.resolved}</td>
                                        <td className="issues__num">{r.reopened}</td>
                                        <td className="issues__num">{r.proposed + r.requeued}</td>
                                        <td className="issues__num">{duration(r.durationMs)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </section>
    );
};

const Tile = ({ label, value, tone, hint, onClick }: { label: string; value: number; tone: string; hint: string; onClick?: () => void }) =>
    onClick ? (
        <button type="button" className={`scanner__tile scanner__tile--${value === 0 ? 'muted' : tone} scanner__tile--link`} title={hint} onClick={onClick}>
            <strong>{value}</strong>
            <span>{label}</span>
        </button>
    ) : (
        <div className={`scanner__tile scanner__tile--${value === 0 ? 'muted' : tone}`} title={hint}>
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    );

const State = ({ status, round, running, stopped }: { status: SweeperStatus; round: SweepRound | null; running: boolean; stopped: boolean }) => {
    if (running) {
        return <span className="scanner__state scanner__state--running">bezig</span>;
    }

    if (round?.stale) {
        return (
            <span className="scanner__state scanner__state--error" title="De API die deze ronde draaide is halverwege gestopt of herstart. Doorgaan slaat over wat al klaar was.">
                onderbroken
            </span>
        );
    }

    if (stopped) {
        return <span className="scanner__state scanner__state--warn">gestopt</span>;
    }

    if (round?.state === 'Failed' || (round?.failed ?? 0) > 0) {
        return <span className="scanner__state scanner__state--error">fouten</span>;
    }

    if (!status.enabled) {
        return <span className="scanner__state">timer uit</span>;
    }

    if (status.pause?.paused) {
        return (
            <span className="scanner__state scanner__state--warn">
                gepauzeerd{status.pause.changedBy ? ` door ${status.pause.changedBy}` : ''}
                {status.pause.changedAt ? ` sinds ${moment(status.pause.changedAt)}` : ''}
            </span>
        );
    }

    return (
        <span className="scanner__state scanner__state--ok">
            wacht{round?.nextRunAt ? ` · volgende ronde ${moment(round.nextRunAt)}` : ''}
        </span>
    );
};

const SCOPES: Record<ScanMode, string> = {
    prod: 'Alleen productie',
    test: 'Alleen test',
    all: 'Alle omgevingen',
    selected: 'Gekozen omgevingen',
};

/**
 * Which environments the timer scans. Production in normal operation; for trying the scanner
 * out, one or a few chosen environments. A round started by hand follows the switch at the top
 * of the page instead, and one environment can always be scanned from its own card.
 */
const ScopeEditor = ({
    scope,
    environments,
    busy,
    onSave,
}: {
    scope: ScanScope;
    environments: IssueEnvironment[];
    busy: boolean;
    onSave: (mode: ScanMode, environments: string[]) => void;
}) => {
    const [mode, setMode] = useState<ScanMode>(scope.mode);
    const [chosen, setChosen] = useState<Set<string>>(new Set(scope.environments));
    const [filter, setFilter] = useState<string>('');
    // The saved scope the choice on screen started from. The panel reloads every few seconds
    // with a new object; only a scope that was really saved anew is taken over, and never over
    // a choice that is not saved yet.
    const [base, setBase] = useState<ScanScope>(scope);

    useEffect(() => {
        if (takeOverSaved(base, scope, { mode, environments: [...chosen] })) {
            setBase(scope);
            setMode(scope.mode);
            setChosen(new Set(scope.environments));
        }
    }, [scope, base, mode, chosen]);

    const changed =
        mode !== scope.mode ||
        (mode === 'selected' &&
            (chosen.size !== scope.environments.length || scope.environments.some((e) => !chosen.has(e))));

    const shown = useMemo(
        () =>
            [...environments]
                .filter((e) => !filter || environmentKey(e.customer, e.environment).includes(filter.toLowerCase()))
                .sort((a, b) => a.customer.localeCompare(b.customer) || a.environment.localeCompare(b.environment)),
        [environments, filter]
    );

    const toggle = (key: string) => {
        const next = new Set(chosen);

        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }

        setChosen(next);
    };

    return (
        <div className="scanner__scope">
            <label>
                <span>Timer scant</span>
                <select
                    className="form-select form-select-sm"
                    value={mode}
                    onChange={(event) => setMode(event.target.value as ScanMode)}
                    disabled={busy}
                >
                    {Object.entries(SCOPES).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            </label>
            {mode === 'selected' && (
                <div className="scanner__scope-list">
                    <input
                        className="form-control form-control-sm"
                        type="search"
                        placeholder="Zoek een klant of omgeving"
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                    />
                    <ul>
                        {shown.map((e) => {
                            const key = environmentKey(e.customer, e.environment);

                            return (
                                <li key={key}>
                                    <label>
                                        <input type="checkbox" checked={chosen.has(key)} onChange={() => toggle(key)} /> {e.customer} /{' '}
                                        {e.environment}
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                    <span className="issues__muted">{chosen.size} gekozen</span>
                </div>
            )}
            {changed && (
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={busy || (mode === 'selected' && chosen.size === 0)}
                    onClick={() => onSave(mode, mode === 'selected' ? [...chosen] : [])}
                >
                    Opslaan
                </button>
            )}
            {!changed && scope.changedBy && (
                <span className="issues__muted">
                    ingesteld door {scope.changedBy} op {moment(scope.changedAt)}
                </span>
            )}
        </div>
    );
};
